import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowClockwise, Star, CheckCircle, XCircle, Trash, Quotes } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUS_STYLES = {
  pending: "bg-accent/10 text-accent border-accent/30",
  approved: "bg-primary/10 text-primary border-primary/30",
  rejected: "bg-muted text-muted-foreground border-border",
};

export default function ReviewsAdmin() {
  const [reviews, setReviews] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/reviews/all").then((r) => setReviews(r.data)).catch(() => toast.error("Could not load reviews."));

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/reviews/${id}`, { status });
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(status === "approved" ? "Review published on the site." : "Review updated.");
    } catch {
      toast.error("Could not update review.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted.");
    } catch {
      toast.error("Could not delete review.");
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-primary tracking-tight">Reviews</h1>
          <p className="mt-2 text-sm text-foreground/60" data-testid="admin-reviews-count">
            {reviews ? `${reviews.length} submission${reviews.length === 1 ? "" : "s"} · ${reviews.filter((r) => r.status === "approved").length} live` : "Loading…"}
          </p>
        </div>
        <button
          data-testid="admin-reviews-refresh"
          onClick={load}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300"
        >
          <ArrowClockwise size={15} weight="bold" /> Refresh
        </button>
      </div>

      <div className="mt-10 space-y-4" data-testid="admin-reviews-list">
        {reviews && reviews.length === 0 && (
          <div className="bg-card border border-border rounded-sm px-8 py-14 text-center" data-testid="admin-reviews-empty">
            <Quotes size={34} weight="duotone" className="text-muted-foreground mx-auto" />
            <p className="mt-4 text-sm text-foreground/50 font-medium">No submissions yet — the "Share your outcome" form feeds this queue.</p>
          </div>
        )}
        <AnimatePresence>
          {reviews &&
            reviews.map((r) => (
              <motion.div
                key={r.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-card border border-border rounded-sm p-7"
                data-testid={`admin-review-row-${r.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start gap-5">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} size={14} weight={i < r.rating ? "fill" : "regular"} className={i < r.rating ? "text-accent" : "text-border"} />
                        ))}
                      </span>
                      <span className={`text-[11px] font-bold uppercase tracking-wider border rounded-full px-3 py-1 ${STATUS_STYLES[r.status]}`} data-testid={`admin-review-status-${r.id}`}>
                        {r.status}
                      </span>
                    </div>
                    <p className="font-serif text-xl text-primary tracking-tight mt-3">{r.outcome}</p>
                    <p className="mt-2 text-sm text-foreground/70 leading-relaxed">"{r.quote}"</p>
                    <p className="mt-3 text-xs text-foreground/45 font-semibold">
                      {r.name}{r.location ? ` · ${r.location}` : ""}{r.brand ? ` · ${r.brand} owner` : ""} · {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                  </div>
                  <div className="flex md:flex-col gap-2.5 shrink-0">
                    {r.status !== "approved" && (
                      <button
                        data-testid={`admin-review-approve-${r.id}`}
                        onClick={() => setStatus(r.id, "approved")}
                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary-foreground bg-primary rounded-full px-4 py-2 hover:bg-accent transition-colors duration-300"
                      >
                        <CheckCircle size={13} weight="bold" /> Approve
                      </button>
                    )}
                    {r.status !== "rejected" && (
                      <button
                        data-testid={`admin-review-reject-${r.id}`}
                        onClick={() => setStatus(r.id, "rejected")}
                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-foreground/60 border border-border rounded-full px-4 py-2 hover:border-primary hover:text-primary transition-colors duration-300"
                      >
                        <XCircle size={13} weight="bold" /> Reject
                      </button>
                    )}
                    {confirmDelete === r.id ? (
                      <button
                        data-testid={`admin-review-confirm-delete-${r.id}`}
                        onClick={() => remove(r.id)}
                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-destructive-foreground bg-destructive rounded-full px-4 py-2"
                      >
                        <Trash size={13} weight="bold" /> Confirm
                      </button>
                    ) : (
                      <button
                        data-testid={`admin-review-delete-${r.id}`}
                        onClick={() => setConfirmDelete(r.id)}
                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-destructive border border-destructive/30 rounded-full px-4 py-2 hover:bg-destructive/10 transition-colors duration-300"
                      >
                        <Trash size={13} weight="bold" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
